import { Subscription, Update } from '../mvuReducer'
import * as MicrophoneInput from './microphoneInput/microphoneInput'
import * as WebRecorder2PubSub from './pubSub'
import * as GlobalPubSub from '../pubSub'
import { Toast } from '~/toasts/toasts'
import * as RecordingStorage from './recordingStorage'
import * as Recorder from './recorder'
import { drop, take } from '~/utils/map'
import { assert } from '~/utils/assert'
import { audioMimeTypeToFileExtension } from '~/utils/mime'
import { Init } from '../mvuReducer'
import * as Connectivity from '~/utils/connectivity'

export const FOUR_HOURS_IN_SECONDS = 4 * 60 * 60
export const recordingCountdownSeconds = 3
const secondsBeforeNoAudioAlert = recordingCountdownSeconds + 2

export type UploadAttrs = {
  recordingStartedAtUnixMs: number
  mimeType: string
  subjectId: string
  audioChunks: Blob[]
}

export type Upload = (attrs: UploadAttrs) => Promise<void> | undefined

type SharedState = {
  clientId: string
  disclosureAudio?: HTMLAudioElement
  disclosureAudioPlayed: boolean

  microphoneInputState: MicrophoneInput.State
  errorMessage?: string
  isOnline: boolean
  otherLocalClientIsRecording: boolean
  maxDurationSeconds: number
  fiveMinuteWarningShown: boolean
}

const sharedProps: (keyof SharedState)[] = [
  'clientId',
  'disclosureAudio',
  'disclosureAudioPlayed',
  'microphoneInputState',
  'errorMessage',
  'isOnline',
  'otherLocalClientIsRecording',
  'maxDurationSeconds',
  'fiveMinuteWarningShown',
]

type SharedStorageState = SharedState & { storage: RecordingStorage.State }
const sharedStorageProps: (keyof SharedStorageState)[] = [...sharedProps, 'storage']

export type StartRecordingPolicy = 'immediately' | 'ifNoPreviousRecordingExists' | 'no'

export type InitialState = SharedState & {
  mode: 'initial'
  startRecording: StartRecordingPolicy
  initializingSubjectId?: string
}

export type InitializedState = SharedStorageState & { mode: 'initialized'; initializingStream: boolean }
export type PlayingDisclosureState = SharedStorageState & { mode: 'playing-disclosure' }
export type CountdownState = SharedStorageState & {
  mode: 'countdown'
  recordingStartTimeMs: number
  initializingStream: boolean
  stream?: MediaStream
  streamingDeviceId?: string
}
export type RecordingState = SharedStorageState & {
  mode: 'recording'
  recorder: Recorder.ActiveState
  lastReceivedAudioAtMs: number
}
export type RecordingPausedState = SharedStorageState & {
  mode: 'paused'
  recorder: Recorder.PausedState
  resuming: boolean
}
export type UploadingState = SharedStorageState & {
  mode: 'uploading'
  recorder?: Recorder.PausedState
  progressPercent: number
}
export type RecordingCompleteState = SharedStorageState & { mode: 'complete' }

export type WebRecorder2State =
  | InitialState
  | InitializedState
  | PlayingDisclosureState
  | RecordingState
  | RecordingPausedState
  | CountdownState
  | UploadingState
  | RecordingCompleteState

type StartNewRecording = { action: 'start-new-recording' }
type DisclosureAudioEnded = { action: 'disclosure-audio-ended' }
type ScheduledRecording = {
  action: 'scheduled-recording'
  startAt: number
  streamingDeviceId?: string
  stream: MediaStream
}
type StartedRecording = { action: 'started-recording'; recorder: Recorder.ActiveState }
type PauseRecording = { action: 'pause-recording' }
type ResumeRecording = { action: 'resume-recording' }
type RecorderPaused = { action: 'recorder-paused'; recorder: Recorder.PausedState }
type RecorderResumed = { action: 'recorder-resumed'; recorder: Recorder.ActiveState }
type StreamOpened = { action: 'stream-opened'; stream: MediaStream }
type StreamUpdated = { action: 'stream-updated'; recorder: Recorder.ActiveState | Recorder.PausedState }
type SubmitRecording = { action: 'submit-recording' }
type UploadCompleted = { action: 'upload-completed' }
type UploadFailed = { action: 'upload-failed' }
type DownloadRecording = { action: 'download-recording' }
type DeleteRecording = { action: 'delete-recording'; subjectId?: string }
type RecordingDeleted = { action: 'recording-deleted' }
type StorageInitialized = { action: 'storage-initialized'; storage: RecordingStorage.State }
type HandleErrorMessage = { action: 'handle-error-message'; message: string }
type LoadFromDisk = { action: 'load-from-disk'; file: File }
type ResetToInitialized = { action: 'reset-to-initialized' }
type TickUpdate = { action: 'tick-update' }
type WentOffline = { action: 'went-offline' }
type CameOnline = { action: 'came-online' }

// See comments on InitializeStream for why this occurs separately from
// initializing the recording.
type StreamInitialized = {
  action: 'stream-initialized'
  stream: MediaStream
  streamingDeviceId?: string
  startPaused: boolean
  nextStep: 'schedule-recording' | 'initialize-recording'
  prevRecorder?: Recorder.ActiveState | Recorder.PausedState
}

export type SetSubjectId = {
  action: 'set-subject-id'
  subjectId: string
  startRecording?: StartRecordingPolicy
}

type SetMaxDuration = { action: 'set-max-duration'; maxDurationSeconds: number }
type UpdateUploadProgress = { action: 'update-upload-progress'; progressPercent: number }
type SetRecordingClientId = { action: 'set-recording-client-id'; clientId?: string }

export type WebRecorder2Action =
  | StartNewRecording
  | DisclosureAudioEnded
  | ScheduledRecording
  | StartedRecording
  | PauseRecording
  | ResumeRecording
  | RecorderPaused
  | RecorderResumed
  | StreamOpened
  | StreamUpdated
  | SubmitRecording
  | UploadCompleted
  | UploadFailed
  | DownloadRecording
  | DeleteRecording
  | RecordingDeleted
  | StorageInitialized
  | HandleErrorMessage
  | LoadFromDisk
  | ResetToInitialized
  | MicrophoneInput.Action
  | StreamInitialized
  | TickUpdate
  | WentOffline
  | CameOnline
  | SetSubjectId
  | SetMaxDuration
  | UpdateUploadProgress
  | SetRecordingClientId

export type Dispatch = (action: WebRecorder2Action) => void

type ScheduleRecordingEffect = {
  effect: 'schedule-recording'
  streamingDeviceId?: string
  selectedDeviceId?: string
  devices: MediaDeviceInfo[]
  storage: RecordingStorage.State
  stream: MediaStream
  prevRecorder?: Recorder.ActiveState | Recorder.PausedState
}

type SubmitWebRecordingEffect = { effect: 'submit-web-recording'; storage: RecordingStorage.State; mimeType: string }
type SubmitFileEffect = { effect: 'submit-file'; subjectId: string; file: File }

type InitializeStorageEffect = {
  effect: 'initialize-storage'
  subjectId: string
  clientId: string
  prevRecorder?: Recorder.ActiveState | Recorder.PausedState
}

type PlayDisclosureAudioEffect = { effect: 'play-disclosure-audio'; audio: HTMLAudioElement }
type LogMessageEffect = { effect: 'log'; message: string }
type DownloadEffect = { effect: 'download'; storage: RecordingStorage.State }
type PauseRecordingEffect = { effect: 'pause-recording'; recorder: Recorder.ActiveState }
type ResumeRecordingEffect = { effect: 'resume-recording'; recorder: Recorder.PausedState; deviceId?: string }
type CriticalAlertEffect = { effect: 'critical-alert'; message: string }
type ClearCriticalAlertEffect = { effect: 'clear-critical-alert' }
type ShowToastEffect = { effect: 'show-toast'; toast: Toast }

type ChangeInputEffect = {
  effect: 'change-input'
  recorder: Recorder.ActiveState | Recorder.PausedState
  deviceId?: string
  devices: MediaDeviceInfo[]
}

type SwapStreamEffect = {
  effect: 'swap-stream'
  recorder: Recorder.PausedState | Recorder.ActiveState
  stream: MediaStream
}

// Irritatingly, we have to initialize the stream first with the *default* audio input device,
// enumerate the devices, and then (within `schedule-recording` or `initialize-recording`)
// switch to the device you had previously selected in your prefs.
//
// The inability to immediately initialize the recording using your preferred input device
// is a consequence of macOS's privacy settings: we can't tell until *after* you've given
// mic access whether your preferred device is available, and if it is not, we need to
// explicitly initialize the stream with the device called `default` for maximum reliability
// in Chrome. (That's a detail handled by `Recorder.initializeStream()`.)
type InitializeStream = {
  effect: 'initialize-stream'
  selectedDeviceId?: string
  devices: MediaDeviceInfo[]
  startPaused: boolean
  prevRecorder?: Recorder.ActiveState | Recorder.PausedState
  nextStep: 'schedule-recording' | 'initialize-recording'
}

// This is what we do when the user reloads the page and we have a recording
// already in the local database. Depending on whether they were paused when
// they left the page, we either continue recording immediately or set up the
// recording in a paused state.
//
// Note that this is a separate effect from `schedule-recording`, because the
// latter gets triggered ultimately by the user clicking the "record" button on
// a fresh recording. In contrast, when you reload a page with a recording in
// the local database, we set up the recording (and ask for mic permissions)
// automatically and immediately.
type InitializeRecordingEffect = {
  effect: 'initialize-recording'
  stream: MediaStream
  storage: RecordingStorage.State
  startPaused: boolean
  streamingDeviceId?: string
  selectedDeviceId?: string
  devices: MediaDeviceInfo[]
  prevRecorder?: Recorder.ActiveState | Recorder.PausedState
}

type DestroyRecordingEffect = {
  effect: 'destroy-recording'
  recorder?: Recorder.ActiveState | Recorder.PausedState
  storage: RecordingStorage.State
}

type StartRecordingEffect = {
  effect: 'start-recording'
  stream?: MediaStream
  storage: RecordingStorage.State
  streamingDeviceId?: string
  selectedDeviceId?: string
}

export type WebRecorder2SideEffect =
  | ScheduleRecordingEffect
  | SubmitWebRecordingEffect
  | SubmitFileEffect
  | MicrophoneInput.SideEffect
  | InitializeStorageEffect
  | LogMessageEffect
  | DownloadEffect
  | InitializeRecordingEffect
  | PauseRecordingEffect
  | ResumeRecordingEffect
  | ChangeInputEffect
  | SwapStreamEffect
  | DestroyRecordingEffect
  | PlayDisclosureAudioEffect
  | InitializeStream
  | StartRecordingEffect
  | CriticalAlertEffect
  | ClearCriticalAlertEffect
  | ShowToastEffect

export type Callbacks = {
  upload: Upload
  log: (message: string) => void
  trackAnalytics: (message: string, extra?: Record<string, any>) => void
  serverLog: (level: 'debug' | 'info' | 'warn', message: string, metadata?: Record<string, any>) => void
  setCriticalBanner: (message: string) => void
  clearCriticalBanner: () => void
  broadcastRecordingState: (state: Recorder.AudioState) => void
  setSubjectId: (subjectId: string) => void
}

export function initialize({
  canUseWebRecorder,
  recordingDisclosureAudioPath,
  clientId,
  startRecording = 'no',
  subjectId,
  maxDurationSeconds = FOUR_HOURS_IN_SECONDS,
}: {
  // If false, we won't listen for events that would start a recording.
  canUseWebRecorder: boolean
  // A unique ID for this connection to the server; this will change across
  // page reloads, browsers, and browser tabs.
  clientId: string
  // The path to the disclosure audio file; this will be stable across page reloads.
  recordingDisclosureAudioPath: string
  // True if the user arrived on this page wants to start recording now
  startRecording: StartRecordingPolicy
  // Optional subject ID (meeting ID or integration reference ID) that must be set before
  // recording can begin. This should be stable across page reloads and different browsers.
  subjectId?: string
  // Maximum recording duration in seconds before auto-stop (default: 4 hours)
  maxDurationSeconds?: number
}): Init<WebRecorder2State, WebRecorder2SideEffect, WebRecorder2Action> {
  const {
    state: microphoneInputState,
    sideEffects: microphoneInputEffects,
    subscriptions: microphoneInputSubscriptions,
  } = MicrophoneInput.initialize()

  const sideEffects: WebRecorder2SideEffect[] = [...microphoneInputEffects]

  if (subjectId) {
    sideEffects.push({ effect: 'initialize-storage', subjectId, clientId })
  }

  const subscriptions: Subscription<WebRecorder2Action>[] = [
    ...microphoneInputSubscriptions,
    {
      type: 'tick',
      intervalMs: 1000,
      action: { action: 'tick-update' },
    },
    { type: 'wentOffline', action: { action: 'went-offline' } },
    { type: 'cameOnline', action: { action: 'came-online' } },
  ]

  if (!Connectivity.isOnline()) {
    debugError('No internet connection detected')
  }

  const disclosureAudio = recordingDisclosureAudioPath ? new Audio(recordingDisclosureAudioPath) : undefined

  disclosureAudio?.addEventListener('loadeddata', () => {
    debugLog('Disclosure audio loaded from', recordingDisclosureAudioPath)
  })

  disclosureAudio?.addEventListener('error', (e) => {
    debugError('Error loading disclosure audio from', recordingDisclosureAudioPath, e)
  })

  return {
    state: {
      mode: 'initial',
      clientId,
      initializingSubjectId: subjectId,
      disclosureAudio,
      disclosureAudioPlayed: !recordingDisclosureAudioPath,
      microphoneInputState,
      isOnline: Connectivity.isOnline(),
      otherLocalClientIsRecording: false,
      startRecording,
      maxDurationSeconds,
      fiveMinuteWarningShown: false,
    },
    sideEffects: canUseWebRecorder ? sideEffects : [],
    subscriptions: canUseWebRecorder ? subscriptions : [],
  }
}

export function applyAction(
  state: WebRecorder2State,
  action: WebRecorder2Action
): Update<WebRecorder2State, WebRecorder2SideEffect> {
  switch (action.action) {
    case 'start-new-recording': {
      assert(state.mode === 'initialized', `Cannot start new recording from non-initialized state ${state.mode}`)
      assert(!('recorder' in state), 'Cannot start new recording with an existing recorder')
      debugLog('Clicked start recording button')

      return applyStartNewRecordingAction(state)
    }

    case 'disclosure-audio-ended': {
      assert(state.mode === 'playing-disclosure', `Cannot handle disclosure ended from non-playing state ${state.mode}`)
      assert(!('recorder' in state), 'Disclosure should not play with an existing recorder')
      return transitionToCountdown(state)
    }

    case 'scheduled-recording': {
      assert(state.mode === 'countdown', `Recording scheduled from non-countdown state ${state.mode}`)

      return [
        {
          ...state,
          mode: 'countdown',
          recordingStartTimeMs: action.startAt,
          stream: action.stream,
          streamingDeviceId: action.streamingDeviceId,
        },
        [],
      ]
    }

    case 'started-recording': {
      assert(
        state.mode === 'countdown' || state.mode === 'initialized' || state.mode === 'paused',
        `Cannot start recording from non-initialized state ${state.mode}`
      )

      return [
        {
          ...take(state, sharedStorageProps),
          mode: 'recording',
          recorder: action.recorder,
          lastReceivedAudioAtMs: Date.now(),
          fiveMinuteWarningShown: false,
        },
        [],
      ]
    }

    case 'pause-recording': {
      assert(state.mode === 'recording', `Cannot pause from non-recording state ${state.mode}`)

      return [
        state,
        [
          { effect: 'pause-recording', recorder: state.recorder },
          { effect: 'log', message: '[Web Recorder] Stopped recording' },
        ],
      ]
    }

    case 'resume-recording': {
      assert(state.mode === 'paused', `Cannot resume from non-paused state ${state.mode}`)
      return applyResumeRecordingAction(state)
    }

    case 'recorder-paused': {
      assert('storage' in state, `Cannot pause from non-initialized state ${state.mode}`)

      return [
        {
          ...take(state, sharedStorageProps),
          mode: 'paused',
          recorder: action.recorder,
          resuming: false,
        },
        [],
      ]
    }

    case 'recorder-resumed': {
      assert(state.mode === 'paused', `Cannot resume from non-paused state ${state.mode}`)

      return [
        {
          ...drop(state, ['resuming']),
          mode: 'recording',
          recorder: action.recorder,
          lastReceivedAudioAtMs: Date.now(),
          fiveMinuteWarningShown: state.fiveMinuteWarningShown,
        },
        [],
      ]
    }

    case 'stream-opened': {
      assert(
        state.mode === 'recording' || state.mode === 'paused',
        `Cannot update stream from non-streaming state ${state.mode}`
      )

      return [state, [{ effect: 'swap-stream', recorder: state.recorder, stream: action.stream }]]
    }

    case 'stream-updated': {
      if (state.mode === 'recording') {
        assert('startTimeMs' in action.recorder, 'Expected recording to be in progress')
        return [{ ...state, recorder: action.recorder }, []]
      } else if (state.mode === 'paused') {
        assert(!('startTimeMs' in action.recorder), 'Expected no recording to be in progress')
        return [{ ...state, recorder: action.recorder }, []]
      } else {
        return [state, []]
      }
    }

    case 'submit-recording': {
      assert(state.mode === 'paused', `Cannot submit from non-paused state ${state.mode}`)
      assert('storage' in state, 'Cannot submit without storage')

      if (state.resuming) {
        return [state, []]
      } else {
        return [
          { ...drop(state, ['resuming']), mode: 'uploading', progressPercent: 0 },
          [
            {
              effect: 'submit-web-recording',
              storage: state.storage,
              mimeType: Recorder.mimeType(state.recorder),
            },
          ],
        ]
      }
    }

    case 'upload-completed': {
      assert(state.mode === 'uploading', `Upload completed in non-uploading state ${state.mode}`)
      return [{ ...state, mode: 'complete' }, []]
    }

    case 'upload-failed': {
      assert('storage' in state, 'Cannot handle upload failure without storage')

      const shared: SharedStorageState = take(state, sharedStorageProps)

      if ('recorder' in state && state.recorder) {
        if ('startTimeMs' in state.recorder) {
          return [
            {
              ...shared,
              mode: 'recording',
              recorder: state.recorder,
              lastReceivedAudioAtMs: Date.now(),
              fiveMinuteWarningShown: shared.fiveMinuteWarningShown,
            },
            [],
          ]
        } else {
          return [
            {
              ...shared,
              mode: 'paused',
              recorder: state.recorder,
              resuming: 'resuming' in state && state.resuming,
            },
            [],
          ]
        }
      } else {
        return [
          {
            ...shared,
            mode: 'initialized',
            initializingStream: 'initializingStream' in state && state.initializingStream,
          },
          [],
        ]
      }
    }

    case 'download-recording': {
      // Separate assert for initial state to persuade tsc that storage is initialized
      assert(state.mode !== 'initial', 'Cannot download from initial state')
      assert('storage' in state, 'Cannot download without storage')
      return [state, [{ effect: 'download', storage: state.storage }]]
    }

    case 'delete-recording': {
      // Separate assert for initial state to persuade tsc that storage is initialized
      assert(state.mode !== 'initial', 'Cannot delete from initial state')

      if (action.subjectId === state.storage.subjectId || action.subjectId === undefined) {
        return [
          state,
          [
            {
              effect: 'destroy-recording',
              recorder: 'recorder' in state ? state.recorder : undefined,
              storage: state.storage,
            },
            { effect: 'log', message: 'Clicked reset recording' },
          ],
        ]
      } else {
        debugLog(`[Web Recorder] Ignoring delete request for inactive subjectId ${action.subjectId}`)
        return [state, []]
      }
    }

    case 'recording-deleted': {
      assert(state.mode !== 'initial', 'Cannot delete from initial state')

      return [
        {
          ...take(state, sharedStorageProps),
          mode: 'initialized',
          initializingStream: 'initializingStream' in state && state.initializingStream,
          disclosureAudioPlayed: !state.disclosureAudio,
        },
        [],
      ]
    }

    case 'storage-initialized': {
      const alreadySubmitted = RecordingStorage.getRecordingSubmitted(action.storage)
      const hasRecordingData = RecordingStorage.getDurationMs(action.storage) > 0

      if (alreadySubmitted) {
        return [{ ...state, mode: 'complete', storage: action.storage }, []]
      } else if (state.mode === 'initial' && !hasRecordingData && state.startRecording !== 'no') {
        return applyStartNewRecordingAction({
          ...state,
          mode: 'initialized',
          storage: action.storage,
          initializingStream: false,
        })
      } else {
        const shouldAlreadyBeRecording =
          (RecordingStorage.getIsRecording(action.storage) ||
            ('startRecording' in state && state.startRecording === 'immediately')) &&
          !Recorder.isOtherClientRecording(action.storage)

        const needsStream =
          !Recorder.isOtherClientRecording(action.storage) && (shouldAlreadyBeRecording || hasRecordingData)

        if (needsStream) {
          debugLog(
            `Continuing recording ${action.storage.subjectId};`,
            shouldAlreadyBeRecording ? 'will start immediately' : 'starting paused'
          )
        }

        const sideEffects: WebRecorder2SideEffect[] = needsStream
          ? [
              {
                effect: 'initialize-stream',
                nextStep: 'initialize-recording',
                startPaused: !shouldAlreadyBeRecording,
                selectedDeviceId: state.microphoneInputState.selectedDeviceId,
                devices: state.microphoneInputState.availableInputs,
                prevRecorder: 'recorder' in state ? state.recorder : undefined,
              },
            ]
          : []

        return [
          { ...state, mode: 'initialized', storage: action.storage, initializingStream: needsStream },
          sideEffects,
        ]
      }
    }

    case 'handle-error-message': {
      debugError(`[Web Recorder] ${action.message}`)
      return [{ ...state, errorMessage: action.message }, []]
    }

    case 'load-from-disk': {
      assert('storage' in state, 'Cannot load file without storage')
      assert(
        ['initialized', 'recording', 'countdown', 'paused'].includes(state.mode),
        `Cannot load file in state ${state.mode}`
      )

      return [
        { ...state, mode: 'uploading', progressPercent: 0 },
        [{ effect: 'submit-file', subjectId: state.storage.subjectId, file: action.file }],
      ]
    }

    case 'reset-to-initialized': {
      assert(state.mode !== 'initial', 'Cannot reset from initial state')

      return [
        {
          ...take(state, sharedStorageProps),
          mode: 'initialized',
          initializingStream: false,
          disclosureAudioPlayed: !state.disclosureAudio,
        },
        [],
      ]
    }

    case 'stream-initialized': {
      assert('storage' in state, 'Cannot initialize stream without storage')
      debugLog('Stream initialized; moving to', action.nextStep)
      const newState = 'initializingStream' in state ? { ...state, initializingStream: false } : state

      if (action.nextStep === 'schedule-recording') {
        return [
          newState,
          [
            {
              effect: 'schedule-recording',
              stream: action.stream,
              streamingDeviceId: action.streamingDeviceId,
              selectedDeviceId: state.microphoneInputState.selectedDeviceId,
              devices: state.microphoneInputState.availableInputs,
              storage: state.storage,
              prevRecorder: 'recorder' in state ? state.recorder : undefined,
            },
          ],
        ]
      } else {
        return [
          newState,
          [
            {
              effect: 'initialize-recording',
              stream: action.stream,
              storage: state.storage,
              streamingDeviceId: action.streamingDeviceId,
              selectedDeviceId: state.microphoneInputState.selectedDeviceId,
              devices: state.microphoneInputState.availableInputs,
              prevRecorder: 'recorder' in state ? state.recorder : undefined,
              startPaused: action.startPaused,
            },
          ],
        ]
      }
    }

    case 'tick-update': {
      const now = Date.now()

      if (state.mode === 'countdown' && now >= state.recordingStartTimeMs && !state.initializingStream) {
        return [
          { ...state, initializingStream: true },
          [
            {
              effect: 'start-recording',
              stream: state.stream,
              storage: state.storage,
              streamingDeviceId: state.streamingDeviceId,
              selectedDeviceId: state.microphoneInputState.selectedDeviceId,
            },
          ],
        ]
      } else if (state.mode === 'recording') {
        // Check if we've exceeded the maximum recording duration
        const currentDurationSeconds = Recorder.recordingDurationSeconds(state.recorder, now)

        // Show 5-minute warning before auto-stop
        const fiveMinutesInSeconds = 5 * 60
        const timeUntilLimitSeconds = state.maxDurationSeconds - currentDurationSeconds
        if (
          !state.fiveMinuteWarningShown &&
          timeUntilLimitSeconds <= fiveMinutesInSeconds &&
          timeUntilLimitSeconds > 0
        ) {
          debugLog('Showing 5-minute warning before auto-stop')
          return [
            { ...state, fiveMinuteWarningShown: true },
            [
              {
                effect: 'show-toast',
                toast: {
                  type: 'warning',
                  content: `Recording will automatically stop in ${Math.ceil(timeUntilLimitSeconds / 60)} minute${Math.ceil(timeUntilLimitSeconds / 60) !== 1 ? 's' : ''}.`,
                  durationMs: 10_000,
                },
              },
            ],
          ]
        } else if (currentDurationSeconds >= state.maxDurationSeconds) {
          debugLog('Maximum recording duration reached, auto-stopping recording')
          return [
            state,
            [
              { effect: 'pause-recording', recorder: state.recorder },
              {
                effect: 'show-toast',
                toast: {
                  type: 'info',
                  content: `Recording automatically stopped after reaching the ${Math.floor(state.maxDurationSeconds / 60)} minute${state.maxDurationSeconds >= 120 ? 's' : ''} limit.`,
                  durationMs: 10_000,
                },
              },
            ],
          ]
        } else if (Recorder.receivingAudioData(state.recorder)) {
          return [{ ...state, lastReceivedAudioAtMs: now }, [{ effect: 'clear-critical-alert' }]]
        } else if (now > state.lastReceivedAudioAtMs + secondsBeforeNoAudioAlert * 1_000) {
          return [
            state,
            [
              {
                effect: 'critical-alert',
                message: `It seems quiet. If someone is speaking, please check your microphone.`,
              },
            ],
          ]
        }
      }

      return [state, []]
    }

    case 'went-offline': {
      if (state.isOnline) {
        debugLog('Went offline')
      }

      if (state.mode === 'uploading') {
        const newState: RecordingPausedState | InitializedState =
          'recorder' in state && state.recorder
            ? { ...state, mode: 'paused', isOnline: false, recorder: state.recorder, resuming: false }
            : {
                ...state,
                mode: 'initialized',
                isOnline: false,
                initializingStream: 'initializingStream' in state && Boolean(state.initializingStream),
              }

        return [
          newState,
          [
            {
              effect: 'show-toast',
              toast: {
                type: 'error',
                content: 'Your computer appears to be offline. Please check your internet connection and try again.',
                durationMs: 10_000,
              },
            },
          ],
        ]
      } else {
        return [{ ...state, isOnline: false }, []]
      }
    }

    case 'came-online':
      if (!state.isOnline) {
        debugLog('Came back online')
      }

      return [{ ...state, isOnline: true }, []]

    case 'set-subject-id': {
      const changesSubjectId =
        ('storage' in state && state.storage.subjectId !== action.subjectId) ||
        ('initializingSubjectId' in state && state.initializingSubjectId !== action.subjectId)

      if (changesSubjectId) {
        debugLog('Setting subject ID', action.subjectId)

        return [
          {
            ...take(state, sharedProps),
            mode: 'initial',
            startRecording: action.startRecording ?? 'no',
            initializingSubjectId: action.subjectId,
            disclosureAudioPlayed: !state.disclosureAudio,
          },
          [
            {
              effect: 'initialize-storage',
              subjectId: action.subjectId,
              clientId: state.clientId,
              prevRecorder: 'recorder' in state ? state.recorder : undefined,
            },
          ],
        ]
      } else if (action.startRecording === 'immediately' && state.mode === 'initialized') {
        return applyStartNewRecordingAction(state)
      } else if (action.startRecording === 'immediately' && state.mode === 'paused' && !state.resuming) {
        return applyResumeRecordingAction(state)
      } else {
        return [state, []]
      }
    }

    case 'update-upload-progress': {
      const newState = state.mode === 'uploading' ? { ...state, progressPercent: action.progressPercent } : state
      return [newState, []]
    }

    case 'set-recording-client-id': {
      return [{ ...state, otherLocalClientIsRecording: !!action.clientId && action.clientId !== state.clientId }, []]
    }

    case 'set-max-duration': {
      debugLog('Setting max recording duration to', action.maxDurationSeconds, 'seconds')
      return [{ ...state, maxDurationSeconds: action.maxDurationSeconds }, []]
    }

    default: {
      if (state.microphoneInputState) {
        const [microphoneInputState, inputSideEffects] = MicrophoneInput.applyAction(state.microphoneInputState, action)

        const changedDevices = microphoneInputState.selectedDeviceId !== state.microphoneInputState.selectedDeviceId

        // If the device changed while we were recording or paused, we need to update the input stream
        const additionalSideEffects: WebRecorder2SideEffect[] =
          changedDevices && (state.mode === 'recording' || state.mode === 'paused')
            ? [
                {
                  effect: 'change-input',
                  recorder: state.recorder,
                  deviceId: microphoneInputState.selectedDeviceId,
                  devices: microphoneInputState.availableInputs,
                },
              ]
            : []

        return [{ ...state, microphoneInputState }, [...inputSideEffects, ...additionalSideEffects]]
      } else {
        return [state, []]
      }
    }
  }
}

function applyStartNewRecordingAction(state: InitializedState): Update<WebRecorder2State, WebRecorder2SideEffect> {
  assert(!('recorder' in state), 'Cannot start new recording with an existing recorder.')

  // Play disclosure audio first if it exists and there's no recording in progress
  if (state.disclosureAudio && !state.disclosureAudioPlayed && RecordingStorage.getDurationMs(state.storage) <= 0) {
    debugLog('Playing disclosure audio first')

    return [
      { ...state, mode: 'playing-disclosure' },
      [{ effect: 'play-disclosure-audio', audio: state.disclosureAudio }],
    ]
  } else {
    return transitionToCountdown(state)
  }
}

function applyResumeRecordingAction(state: RecordingPausedState): Update<WebRecorder2State, WebRecorder2SideEffect> {
  if (state.resuming) {
    return [state, []]
  } else {
    return [
      { ...state, resuming: true },
      [
        {
          effect: 'resume-recording',
          recorder: state.recorder,
          deviceId: state.microphoneInputState.selectedDeviceId,
        },
      ],
    ]
  }
}

function transitionToCountdown(
  state: InitializedState | PlayingDisclosureState
): Update<WebRecorder2State, WebRecorder2SideEffect> {
  return [
    {
      ...state,
      mode: 'countdown',
      recordingStartTimeMs: countdownEndTime(),
      disclosureAudioPlayed: true,
      initializingStream: true,
    },
    [
      {
        effect: 'initialize-stream',
        devices: state.microphoneInputState.availableInputs,
        selectedDeviceId: state.microphoneInputState.selectedDeviceId,
        nextStep: 'schedule-recording',
        prevRecorder: 'recorder' in state && state.recorder ? (state.recorder as Recorder.PausedState) : undefined,
        startPaused: false,
      },
    ],
  ]
}

export function makeSideEffectExecutor({
  trackAnalytics,
  serverLog,
  log,
  upload,
  setCriticalBanner,
  clearCriticalBanner,
  broadcastRecordingState,
  setSubjectId,
}: Callbacks): (effect: WebRecorder2SideEffect, dispatch: Dispatch) => void {
  return async (sideEffect: WebRecorder2SideEffect, dispatch: Dispatch) => {
    // Log all side effects for debugging (moved from analytics to server logs)
    serverLog('info', `web-recorder:side-effect:${sideEffect.effect}`, {
      effect: sideEffect.effect,
      timestamp: Date.now(),
    })

    switch (sideEffect.effect) {
      case 'log': {
        log(sideEffect.message)
        break
      }

      case 'initialize-stream': {
        try {
          Recorder.stopPreviousRecording(sideEffect.prevRecorder || {})

          debugLog(
            'Initializing stream with device',
            MicrophoneInput.debugDeviceName(sideEffect.devices, sideEffect.selectedDeviceId)
          )

          const stream = await Recorder.initializeStream(sideEffect.selectedDeviceId)

          await MicrophoneInput.executeSideEffect({ effect: 'enumerate-media-devices' }, dispatch)

          dispatch({
            action: 'stream-initialized',
            stream,
            streamingDeviceId: sideEffect.selectedDeviceId,
            prevRecorder: sideEffect.prevRecorder,
            nextStep: sideEffect.nextStep,
            startPaused: sideEffect.startPaused,
          })
        } catch (error) {
          debugError('Error initializing input stream', error)
          handleStreamInitializationError(error, log, trackAnalytics, serverLog, setCriticalBanner, dispatch)
        }
        break
      }

      case 'schedule-recording': {
        debugLog(
          'Scheduling recording with device',
          MicrophoneInput.debugDeviceName(sideEffect.devices, sideEffect.selectedDeviceId)
        )

        Recorder.stopPreviousRecording(sideEffect.prevRecorder || {})

        const stream =
          sideEffect.selectedDeviceId === sideEffect.streamingDeviceId
            ? sideEffect.stream
            : await Recorder.initializeStream(sideEffect.selectedDeviceId)

        debugLog(
          'Initialized stream for device',
          MicrophoneInput.debugDeviceName(sideEffect.devices, sideEffect.selectedDeviceId)
        )

        WebRecorder2PubSub.publish('recording-scheduled', {})

        dispatch({
          action: 'scheduled-recording',
          startAt: countdownEndTime(),
          streamingDeviceId: sideEffect.streamingDeviceId,
          stream,
        })

        GlobalPubSub.publish('show-toast', {
          type: 'info',
          content: 'Remember to ask for client consent',
          durationMs: 10_000,
        } as Toast)
        break
      }

      case 'start-recording': {
        const stream =
          sideEffect.stream && sideEffect.selectedDeviceId === sideEffect.streamingDeviceId
            ? sideEffect.stream
            : await Recorder.initializeStream(sideEffect.selectedDeviceId)

        const recorderState = Recorder.startRecording(
          sideEffect.storage,
          stream,
          trackAnalytics,
          broadcastRecordingState
        )

        if ('error' in recorderState) {
          serverLog('warn', 'web-recorder:start-recording-failed', {
            context: 'new-recording',
            error: recorderState.error,
          })
          dispatch({
            action: 'handle-error-message',
            message: recorderState.error || 'Unknown error starting recording',
          })
        } else {
          // Remove capture_now from URL when recording starts to prevent auto-start on refresh
          removeCaptureNowFromUrl()
          WebRecorder2PubSub.publish('recording-started', { subjectId: sideEffect.storage.subjectId })
          dispatch({ action: 'started-recording', recorder: recorderState })
        }
        break
      }

      case 'initialize-recording': {
        debugLog(
          'Initializing recording with device',
          MicrophoneInput.debugDeviceName(sideEffect.devices, sideEffect.streamingDeviceId)
        )

        Recorder.stopPreviousRecording(sideEffect.prevRecorder || {})

        const stream =
          sideEffect.selectedDeviceId === sideEffect.streamingDeviceId
            ? sideEffect.stream
            : await Recorder.initializeStream(sideEffect.selectedDeviceId)

        const initializeResult = Recorder.initializeRecording(
          sideEffect.storage,
          stream,
          trackAnalytics,
          broadcastRecordingState,
          { startPaused: sideEffect.startPaused }
        )

        if ('error' in initializeResult) {
          serverLog('warn', 'web-recorder:start-recording-failed', {
            context: 'existing-recording',
            error: initializeResult.error,
          })
          dispatch({
            action: 'handle-error-message',
            message: initializeResult.error || 'Unknown error initializing recording',
          })
        } else if ('startTimeMs' in initializeResult) {
          // Remove capture_now from URL when recording starts to prevent auto-start on refresh
          removeCaptureNowFromUrl()
          WebRecorder2PubSub.publish('recording-started', { subjectId: sideEffect.storage.subjectId })
          dispatch({ action: 'started-recording', recorder: initializeResult })
        } else {
          WebRecorder2PubSub.publish('recording-started', { subjectId: sideEffect.storage.subjectId })
          dispatch({ action: 'recorder-paused', recorder: initializeResult })
        }
        break
      }

      case 'submit-web-recording':
        try {
          await submitRecording(sideEffect.storage, sideEffect.mimeType, upload, trackAnalytics, log)
          dispatch({ action: 'upload-completed' })
        } catch (error) {
          debugError('Error uploading recording', error)
          handleUploadError(error, log, trackAnalytics, dispatch)
        }
        break

      case 'submit-file': {
        const reader = new FileReader()
        reader.onload = async (e) => {
          const audioData = e.target?.result
          if (audioData) {
            trackAnalytics('recording_uploaded')
            log('Recording uploaded successfully from disk')
            dispatch({ action: 'upload-completed' })

            try {
              const type = sideEffect.file.type

              await upload({
                subjectId: sideEffect.subjectId,
                mimeType: type,
                recordingStartedAtUnixMs: Date.now(),
                audioChunks: [new Blob([audioData], { type })],
              })
            } catch (error) {
              debugError('Error uploading recording from disk', error)
              handleUploadError(error, log, trackAnalytics, dispatch)
            }
          }
        }
        reader.readAsArrayBuffer(sideEffect.file)
        break
      }

      case 'initialize-storage':
        try {
          Recorder.stopPreviousRecording(sideEffect.prevRecorder || {})

          const [storage, _] = await Promise.all([
            RecordingStorage.createRecordingStorage(sideEffect.subjectId, sideEffect.clientId),
            Recorder.otherClientRecordingStateKnown,
          ])

          setSubjectId(sideEffect.subjectId)

          dispatch({ action: 'storage-initialized', storage })
        } catch (error) {
          debugError('Failed to initialize storage:', error)
          log(`Failed to initialize storage: ${error}`)
          serverLog('warn', 'web-recorder:initialize-storage-failed', {})

          const message =
            error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
              ? error.message
              : 'Failed to initialize web audio capture. Please try refreshing the page.'

          dispatch({ action: 'handle-error-message', message })
        }
        break

      case 'download':
        try {
          downloadRecording(sideEffect.storage, 'recording', trackAnalytics, serverLog, log)
        } catch (error) {
          debugError('Error downloading recording', error)
          log(`Failed to download recording: ${error}`)
          serverLog('warn', 'web-recorder:download-local-recording-failed', {})

          dispatch({
            action: 'handle-error-message',
            message: 'Failed to download meeting. Please try again.',
          })
        }
        break

      case 'pause-recording':
        dispatch({ action: 'recorder-paused', recorder: Recorder.pauseRecording(sideEffect.recorder) })
        break

      case 'resume-recording': {
        Recorder.stopPreviousRecording(sideEffect.recorder)
        const stream = await Recorder.initializeStream(sideEffect.deviceId)

        const resumeResult = Recorder.resumeRecording(
          sideEffect.recorder,
          stream,
          trackAnalytics,
          broadcastRecordingState
        )

        if ('error' in resumeResult) {
          serverLog('warn', 'web-recorder:resume-recording-failed', {})
          dispatch({
            action: 'handle-error-message',
            message: resumeResult.error || 'Unknown error resuming recording',
          })
        } else {
          try {
            dispatch({ action: 'recorder-resumed', recorder: resumeResult })
            WebRecorder2PubSub.publish('recording-started', { subjectId: sideEffect.recorder.storage.subjectId })
          } catch (error) {
            debugError('Error resuming recording', error)
            Recorder.stopPreviousRecording(resumeResult)
            handleStreamInitializationError(error, log, trackAnalytics, serverLog, setCriticalBanner, dispatch)
          }
        }
        break
      }

      case 'change-input': {
        try {
          debugLog('Changing input stream to', MicrophoneInput.debugDeviceName(sideEffect.devices, sideEffect.deviceId))

          const stream = await Recorder.initializeStream(sideEffect.deviceId)
          dispatch({ action: 'stream-opened', stream })
        } catch (error) {
          debugError('Error opening input stream', error)
          Recorder.stopPreviousRecording(sideEffect.recorder)
          handleStreamInitializationError(error, log, trackAnalytics, serverLog, setCriticalBanner, dispatch)
        }
        break
      }

      case 'swap-stream': {
        try {
          const recorderState = Recorder.switchInputStream(sideEffect.recorder, sideEffect.stream)
          dispatch({ action: 'stream-updated', recorder: recorderState })
        } catch (error) {
          debugError('Error switching input stream', error)
          Recorder.stopPreviousRecording(sideEffect.recorder)
          handleStreamInitializationError(error, log, trackAnalytics, serverLog, setCriticalBanner, dispatch)
        }
        break
      }

      case 'destroy-recording': {
        if (sideEffect.recorder) {
          Recorder.stopPreviousRecording(sideEffect.recorder)
        }

        RecordingStorage.clear(sideEffect.storage).catch(debugError)
        // Remove capture_now from URL when recording is deleted to prevent auto-start on refresh
        removeCaptureNowFromUrl()
        dispatch({ action: 'recording-deleted' })
        break
      }

      case 'play-disclosure-audio': {
        const audioEndedCallback = () => {
          debugLog('Disclosure audio ended')
          dispatch({ action: 'disclosure-audio-ended' })

          // We need to remove the event listener that dispatches after the audio ends,
          // because the audio element will stick around between recordings. We don't want
          // to fire the `disclosure-audio-ended` action multiple times on future recordings.
          sideEffect.audio.removeEventListener('ended', audioEndedCallback)
        }

        sideEffect.audio.addEventListener('ended', audioEndedCallback)

        sideEffect.audio.play().catch((error) => {
          log(`Failed to play disclosure audio: ${error}`)
          serverLog('warn', 'web-recorder:disclosure-audio-failed', { error: error.toString() })
          // Fall back to starting countdown immediately
          dispatch({ action: 'disclosure-audio-ended' })
        })
        break
      }

      case 'critical-alert':
        debugLog('Setting critical alert', sideEffect.message)
        setCriticalBanner(sideEffect.message)
        break

      case 'clear-critical-alert':
        clearCriticalBanner()
        break

      case 'show-toast':
        GlobalPubSub.publish('show-toast', sideEffect.toast)
        break

      default:
        MicrophoneInput.executeSideEffect(sideEffect, dispatch)
    }
  }
}

export function subjectId(state?: WebRecorder2State): string | undefined {
  if (!state) return undefined
  if ('storage' in state) return state.storage?.subjectId
  if ('initializingSubjectId' in state) return state.initializingSubjectId
  return undefined
}

export async function downloadRecording(
  storage: RecordingStorage.State,
  fileNameWithoutExtension: string,
  trackAnalytics: Callbacks['trackAnalytics'],
  serverLog: Callbacks['serverLog'],
  log: Callbacks['log']
) {
  const blob = await RecordingStorage.downloadableBlob(storage)

  if (blob.size > 0) {
    const mimeType = RecordingStorage.getMimeType(storage)
    const fileExtension = audioMimeTypeToFileExtension(mimeType)
    triggerDownload(blob, `${fileNameWithoutExtension || 'recording'}.${fileExtension}`)
    trackAnalytics('Saved web recording to disk')
    log('Recording downloaded successfully')
  } else {
    log('No audio chunks available to save')
    serverLog('warn', 'web-recorder:download-local-recording-failed', { reason: 'no-audio-chunks' })

    const toast: Toast = { type: 'error', content: 'No meeting audio to download', durationMs: 5000 }
    GlobalPubSub.publish('show-toast', toast)
  }
}

export async function submitRecording(
  storage: RecordingStorage.State,
  mimeType: string,
  upload: Callbacks['upload'],
  trackAnalytics: Callbacks['trackAnalytics'],
  log: Callbacks['log']
) {
  try {
    window.addEventListener('beforeunload', confirmBeforeUnload)

    const audioChunks = await RecordingStorage.getAllChunks(storage)

    await upload({
      subjectId: storage.subjectId,
      mimeType: mimeType,
      recordingStartedAtUnixMs: RecordingStorage.getStartTimeMs(storage),
      audioChunks,
    })

    trackAnalytics('recording_uploaded')
    log('Recording uploaded successfully')
    RecordingStorage.setRecordingSubmitted(storage)
  } finally {
    window.removeEventListener('beforeunload', confirmBeforeUnload)
  }
}

function countdownEndTime() {
  return Date.now() + recordingCountdownSeconds * 1_000
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.style.display = 'none'
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  window.URL.revokeObjectURL(url)
  document.body.removeChild(a)
}

function handleStreamInitializationError(
  error: unknown,
  log: Callbacks['log'],
  trackAnalytics: Callbacks['trackAnalytics'],
  serverLog: Callbacks['serverLog'],
  setCriticalBanner: Callbacks['setCriticalBanner'],
  dispatch: Dispatch
) {
  debugError('Failed to get user media:', error)
  log(`Failed to get user media: ${error}`)
  serverLog('info', 'web-recorder:stream-initialization-failed', { error: String(error) })
  trackAnalytics('recording_failed', { error: String(error) })

  const toast: Toast = { type: 'error', content: 'Failed to start meeting capture', durationMs: 10_000 }
  GlobalPubSub.publish('show-toast', toast)

  setCriticalBanner('Microphone access is required for meeting capture.')

  dispatch({ action: 'reset-to-initialized' })
}

export function handleUploadError(
  error: unknown,
  log: Callbacks['log'],
  trackAnalytics: Callbacks['trackAnalytics'],
  dispatch: Dispatch
) {
  log(`Upload failed: ${error}`)
  trackAnalytics('upload_failed', { error: String(error) })

  // Don't include a durationMs here because we want the toast to persist until the user dismisses it.
  const toast: Toast = { type: 'error', content: 'Failed to upload meeting. Please try again.' }
  GlobalPubSub.publish('show-toast', toast)

  dispatch({ action: 'upload-failed' })
}

// Exported so that in testing, we can mock it to a no-op
export function debugLog(...args: any[]) {
  /* eslint-disable-next-line no-console */
  console.log('[Web Recorder]', ...args)
}

export function debugError(...args: any[]) {
  /* eslint-disable-next-line no-console */
  console.error('[Web Recorder]', ...args)
}

/**
 * Removes the capture_now query parameter from the current URL without page reload
 */
function removeCaptureNowFromUrl() {
  const url = new URL(window.location.href)
  if (url.searchParams.has('capture_now')) {
    url.searchParams.delete('capture_now')
    window.history.replaceState({}, '', url.toString())
  }
}

/**
 * We'll register this as a beforeunload event listener during any critical portions of recording.
 */
function confirmBeforeUnload(e: BeforeUnloadEvent) {
  // Cancel the event and show alert that
  // the unsaved changes would be lost
  e.preventDefault()
  e.returnValue = ''
}
